"use client";

import { useState } from "react";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  LineChart,
  type LineRow,
} from "@/components/tool-kit/charts/line-chart";
import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { CurrencyField, useMoney } from "@/components/tools/finance/shared";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberOr } from "@/lib/parse-number";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type BreakEvenInput,
  type BreakEvenResult,
  breakEven,
  chartMaxUnits,
  volumePoints,
} from "@/lib/tool-kit/finance/break-even";

/** English strings. A translated content file overrides these through `ui`. */
export const BREAK_EVEN_CALCULATOR_STRINGS = {
  inputsHeading: "Your costs and price",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  fixedCosts: "Fixed costs for the period",
  price: "Selling price per unit",
  variableCost: "Variable cost per unit",
  targetProfit: "Target profit (optional)",
  expectedUnits: "Expected sales in units (optional)",
  convention:
    "Fixed costs stay the same at every volume; price and variable cost per unit are constant. Units and costs refer to the same period — a month, a year or a project.",
  resultsHeading: "Results",
  breakEvenUnits: "Break-even units",
  breakEvenRevenue: "Break-even revenue",
  margin: "Contribution margin per unit",
  marginRatio: "Contribution margin ratio",
  targetUnits: "Units for the target profit",
  targetRevenue: "Revenue for the target profit",
  exactUnits: "Exactly {units} units; rounded up to whole units.",
  expectedHeading: "At your expected sales",
  expectedProfit: "Profit at expected sales",
  expectedLoss: "Loss at expected sales",
  safetyUnits: "Margin of safety (units)",
  safetyPercent: "Margin of safety (% of sales)",
  leverage: "Degree of operating leverage",
  belowBreakEven:
    "Expected sales are below break-even, so the margin of safety is negative.",
  workingHeading: "The working",
  workMargin:
    "Contribution margin = price − variable cost = {price} − {variable} = {margin}",
  workRatio: "Contribution margin ratio = {margin} ÷ {price} = {ratio}",
  workUnits:
    "Break-even units = fixed costs ÷ margin = {fixed} ÷ {margin} = {units}",
  workRevenue: "Break-even revenue = {units} × {price} = {revenue}",
  workTarget:
    "Units for target profit = (fixed costs + target) ÷ margin = ({fixed} + {target}) ÷ {margin} = {units}",
  chartHeading: "Revenue and cost by volume",
  chartTitle: "Break-even chart",
  chartXTitle: "Units sold",
  chartYTitle: "Amount",
  chartLabel: "Break-even chart",
  chartDescription:
    "Line chart of revenue, total cost and fixed costs from 0 to {max} units. Revenue and total cost cross at {units} units.",
  seriesRevenue: "Revenue",
  seriesTotalCost: "Total cost",
  seriesFixed: "Fixed costs",
  tableHeading: "Profit by volume",
  unitsColumn: "Units",
  revenueColumn: "Revenue",
  costColumn: "Total cost",
  profitColumn: "Profit",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  noBreakEven:
    "There is no break-even volume: the selling price must be above the variable cost per unit, otherwise each unit sold adds to the loss.",
  needInputs:
    "Enter fixed costs of 0 or more, a selling price above zero and a variable cost of 0 or more.",
} as const;

type Strings = typeof BREAK_EVEN_CALCULATOR_STRINGS;

const STEPS = 10;

interface Form {
  fixed: string;
  price: string;
  variable: string;
  target: string;
  expected: string;
}

function toInput(form: Form): BreakEvenInput | null {
  const fixedCosts = parseNumberInput(form.fixed);
  const price = parseNumberInput(form.price);
  const variableCost = parseNumberInput(form.variable);
  if (
    fixedCosts === null ||
    fixedCosts < 0 ||
    price === null ||
    price <= 0 ||
    variableCost === null ||
    variableCost < 0
  ) {
    return null;
  }
  const expected = parseNumberInput(form.expected);
  return {
    fixedCosts,
    price,
    variableCost,
    targetProfit: Math.max(0, parseNumberOr(form.target, 0)),
    expectedUnits: expected !== null && expected > 0 ? expected : null,
  };
}

function Working({
  input,
  result,
  currency,
  strings,
}: {
  input: BreakEvenInput;
  result: BreakEvenResult;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const num = (v: number) => fmt(v, { maximumFractionDigits: 4 });
  const values = {
    price: money(input.price),
    variable: money(input.variableCost),
    margin: money(result.margin),
    ratio: `${fmt(result.marginRatio * 100, { maximumFractionDigits: 4 })}%`,
    fixed: money(input.fixedCosts),
    target: money(input.targetProfit),
    units: num(result.breakEvenUnits),
    revenue: money(result.breakEvenRevenue),
  };
  const lines = [
    t(strings.workMargin, values),
    t(strings.workRatio, values),
    t(strings.workUnits, values),
    t(strings.workRevenue, values),
    ...(input.targetProfit > 0
      ? [
          t(strings.workTarget, {
            ...values,
            units: num(result.targetUnits),
          }),
        ]
      : []),
  ];
  return (
    <div className={`${CARD} space-y-1`}>
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      {lines.map((line) => (
        <p
          className="font-mono text-muted-foreground text-sm leading-relaxed"
          key={line}
        >
          {line}
        </p>
      ))}
    </div>
  );
}

function ExpectedBlock({
  result,
  currency,
  strings,
}: {
  result: BreakEvenResult;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const e = result.expected;
  if (!e) {
    return null;
  }
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.expectedHeading}</h3>
      <StatGrid>
        <StatCard
          emphasis
          label={e.profit >= 0 ? strings.expectedProfit : strings.expectedLoss}
          value={money(Math.abs(e.profit))}
        />
        <StatCard
          label={strings.safetyUnits}
          value={fmt(e.marginOfSafetyUnits, { maximumFractionDigits: 2 })}
        />
        <StatCard
          label={strings.safetyPercent}
          value={`${fmt(e.marginOfSafety * 100, { maximumFractionDigits: 2 })}%`}
        />
        {e.operatingLeverage === null ? null : (
          <StatCard
            label={strings.leverage}
            value={fmt(e.operatingLeverage, { maximumFractionDigits: 3 })}
          />
        )}
      </StatGrid>
      {e.marginOfSafety < 0 ? <Notice>{strings.belowBreakEven}</Notice> : null}
    </>
  );
}

function VolumeChart({
  input,
  result,
  strings,
}: {
  input: BreakEvenInput;
  result: BreakEvenResult;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const max = chartMaxUnits(result);
  const points = volumePoints(input, max, STEPS);
  const names = [
    strings.seriesRevenue,
    strings.seriesTotalCost,
    strings.seriesFixed,
  ];
  const rows: LineRow[] = points.map((p) => ({
    label: fmt(p.units, { maximumFractionDigits: 2 }),
    x: p.units,
    values: [p.revenue, p.totalCost, input.fixedCosts],
  }));
  const style = {
    title: strings.chartTitle,
    xTitle: strings.chartXTitle,
    yTitle: strings.chartYTitle,
    color: "blue" as const,
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  };
  return (
    <ChartFigure
      apa={false}
      data={toTsv([
        [strings.chartXTitle, ...names],
        ...rows.map((row) => [row.label, ...row.values]),
      ])}
      figureNumber="1"
      filename={chartFilename(strings.chartTitle, "break-even")}
      labels={strings}
      note=""
      table={null}
      title={strings.chartTitle}
    >
      <LineChart
        description={t(strings.chartDescription, {
          max: fmt(max, { maximumFractionDigits: 0 }),
          units: fmt(result.breakEvenUnits, { maximumFractionDigits: 2 }),
        })}
        fmt={fmt}
        id="be-chart"
        label={strings.chartLabel}
        markers={false}
        names={names}
        numeric
        rows={rows}
        style={style}
        zero
      />
    </ChartFigure>
  );
}

function Results({
  input,
  currency,
  strings,
}: {
  input: BreakEvenInput;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const result = breakEven(input);
  if (!result) {
    return <Notice>{strings.noBreakEven}</Notice>;
  }
  const points = volumePoints(input, chartMaxUnits(result), STEPS);
  const exact = result.breakEvenUnits !== result.breakEvenWhole;
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.breakEvenUnits}
          value={fmt(result.breakEvenWhole)}
        />
        <StatCard
          label={strings.breakEvenRevenue}
          value={money(result.breakEvenRevenue)}
        />
        <StatCard label={strings.margin} value={money(result.margin)} />
        <StatCard
          label={strings.marginRatio}
          value={`${fmt(result.marginRatio * 100, { maximumFractionDigits: 2 })}%`}
        />
        {input.targetProfit > 0 ? (
          <StatCard
            label={strings.targetUnits}
            value={fmt(result.targetWhole)}
          />
        ) : null}
        {input.targetProfit > 0 ? (
          <StatCard
            label={strings.targetRevenue}
            value={money(result.targetRevenue)}
          />
        ) : null}
      </StatGrid>
      {exact ? (
        <p className={HINT}>
          {t(strings.exactUnits, {
            units: fmt(result.breakEvenUnits, { maximumFractionDigits: 4 }),
          })}
        </p>
      ) : null}
      <ExpectedBlock currency={currency} result={result} strings={strings} />
      <Working
        currency={currency}
        input={input}
        result={result}
        strings={strings}
      />
      <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
      <VolumeChart input={input} result={result} strings={strings} />
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[
          strings.unitsColumn,
          strings.revenueColumn,
          strings.costColumn,
          strings.profitColumn,
        ]}
        rows={points.map((p) => [
          fmt(p.units, { maximumFractionDigits: 2 }),
          money(p.revenue),
          money(p.totalCost),
          money(p.profit),
        ])}
      />
    </section>
  );
}

export default function BreakEvenCalculatorWidget() {
  const strings = useToolUi(BREAK_EVEN_CALCULATOR_STRINGS);
  const [currency, setCurrency] = useState("USD");
  const [form, setForm] = useState<Form>({
    fixed: "50000",
    price: "25",
    variable: "15",
    target: "20000",
    expected: "8000",
  });
  const update = (patch: Partial<Form>) =>
    setForm((old) => ({ ...old, ...patch }));
  const input = toInput(form);

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <CurrencyField
            id="be-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
          <TextField
            id="be-fixed"
            label={strings.fixedCosts}
            numeric
            onChange={(value) => update({ fixed: value })}
            value={form.fixed}
          />
          <TextField
            id="be-price"
            label={strings.price}
            numeric
            onChange={(value) => update({ price: value })}
            value={form.price}
          />
          <TextField
            id="be-variable"
            label={strings.variableCost}
            numeric
            onChange={(value) => update({ variable: value })}
            value={form.variable}
          />
          <TextField
            id="be-target"
            label={strings.targetProfit}
            numeric
            onChange={(value) => update({ target: value })}
            value={form.target}
          />
          <TextField
            id="be-expected"
            label={strings.expectedUnits}
            numeric
            onChange={(value) => update({ expected: value })}
            value={form.expected}
          />
        </div>
        <p className={HINT}>{strings.convention}</p>
      </section>
      {input ? (
        <Results currency={currency} input={input} strings={strings} />
      ) : (
        <Notice>{strings.needInputs}</Notice>
      )}
    </div>
  );
}
